// src/products/dataroom/components/Modals/TemplateModal.js
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const TemplateModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: "Performance Reporting",
      description: "Folders for quarterly/annual performance reports.",
      folders: ["Q1 Reports", "Q2 Reports", "Q3 Reports", "Q4 Reports", "Annual Summary"]
    },
    {
      id: 2,
      name: "Board Reporting & Governance",
      description: "Standard board meeting and governance docs.",
      folders: ["Board Minutes", "Governance Policies", "Board Resolutions", "Committee Reports"]
    },
    {
      id: 3,
      name: "DealRoom M&A",
      description: "Comprehensive M&A dataroom structure.",
      folders: ["Corporate", "Financial", "Operational", "HR", "Legal", "Commercial", "IP"]
    },
    {
      id: 4,
      name: "DealRoom Capital Raising",
      description: "Folders for fundraising rounds.",
      folders: ["Investor List", "NDAs", "Term Sheets", "Follow-up Notes"]
    },
    {
      id: 5,
      name: "Employee Option/Share Scheme",
      description: "ESOP and share scheme docs.",
      folders: ["Option Agreements", "Grant Schedules", "Vesting Schedules", "Exercise Forms"]
    },
    {
      id: 6,
      name: "Cap Table Updates",
      description: "Cap table tracking and updates.",
      folders: ["Current Cap Table", "Historical Versions", "Issuance Records", "Shareholder Updates"]
    }
  ]);

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    folders: ['']
  });

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
  };

  const handleSaveEdit = () => {
    if (!editingTemplate.name.trim()) return;
    
    setTemplates(prev => prev.map(t => 
      t.id === editingTemplate.id ? editingTemplate : t
    ));
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (templateId) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name.trim() || newTemplate.folders.filter(f => f.trim()).length === 0) return;

    const template = {
      id: Date.now(),
      name: newTemplate.name.trim(),
      description: newTemplate.description.trim(),
      folders: newTemplate.folders.filter(f => f.trim()).map(f => f.trim())
    };

    setTemplates(prev => [...prev, template]);
    setNewTemplate({ name: '', description: '', folders: [''] });
    setShowCreateTemplate(false);
  };

  const addFolder = (templateSetter, template) => {
    templateSetter({
      ...template,
      folders: [...template.folders, '']
    });
  };

  const updateFolder = (templateSetter, template, index, value) => {
    const newFolders = [...template.folders];
    newFolders[index] = value;
    templateSetter({
      ...template,
      folders: newFolders
    });
  };

  const removeFolder = (templateSetter, template, index) => {
    if (template.folders.length <= 1) return;
    templateSetter({
      ...template,
      folders: template.folders.filter((_, i) => i !== index)
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center border-b p-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {showCreateTemplate ? 'Create Template' : editingTemplate ? 'Edit Template' : 'Select Dataroom Template'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {showCreateTemplate ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                <input
                  id="templateName"
                  type="text"
                  name="templateName"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter template name"
                />
              </div>
              
              <div>
                <label htmlFor="templateDescription" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  id="templateDescription"
                  name="templateDescription"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Enter template description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Folders</label>
                {newTemplate.folders.map((folder, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={folder}
                      onChange={(e) => updateFolder(setNewTemplate, newTemplate, index, e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Folder name"
                    />
                    {newTemplate.folders.length > 1 && (
                      <button
                        onClick={() => removeFolder(setNewTemplate, newTemplate, index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addFolder(setNewTemplate, newTemplate)}
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus size={16} className="mr-1" />
                  Add Folder
                </button>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateTemplate(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Template
                </button>
              </div>
            </div>
          ) : editingTemplate ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Folders</label>
                {editingTemplate.folders.map((folder, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={folder}
                      onChange={(e) => updateFolder(setEditingTemplate, editingTemplate, index, e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {editingTemplate.folders.length > 1 && (
                      <button
                        onClick={() => removeFolder(setEditingTemplate, editingTemplate, index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addFolder(setEditingTemplate, editingTemplate)}
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus size={16} className="mr-1" />
                  Add Folder
                </button>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Save size={16} className="mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-medium text-gray-900">Available Templates</h4>
                <button
                  onClick={() => setShowCreateTemplate(true)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Plus size={16} className="mr-2" />
                  Create New
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900">{template.name}</h5>
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded"
                          title="Edit template"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Delete template"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-2">Folders ({template.folders.length}):</p>
                      <div className="text-xs text-gray-500 max-h-20 overflow-y-auto">
                        {template.folders.join(', ')}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => onSelectTemplate(template)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
                    >
                      Use This Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!showCreateTemplate && !editingTemplate && (
          <div className="border-t p-6">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TemplateModal;